/**
 * The one place in the app that talks to MediaPipe.
 *
 * Everything above this file — detectors, adapters, calibration, scenes — sees {@link PoseFrame}
 * and nothing else. That is the structural half of architecture §10/§25: a game cannot inspect
 * raw landmarks because it cannot reach the type, and the platform cannot accidentally couple to
 * MediaPipe's API because exactly one module imports it.
 *
 * Deliberately absent, and deliberately not invented during a spike:
 *  - no camera acquisition (`getUserMedia` is Slice A's, and the engine only ever receives an
 *    element someone else owns);
 *  - no detection loop (who drives frames, and how often, is Slice B's decision);
 *  - no semantic events, no confidence gate, no storage.
 */

import { FilesetResolver, PoseLandmarker } from '@mediapipe/tasks-vision';

import { assertSameOriginAssets, POSE_ENGINE_ASSETS, POSE_ENGINE_DEFAULTS } from './pose-assets';
import { createMonotonicTimestamps, toPoseFrame } from './pose-frame';
import type {
  PoseEngineOptions,
  PoseEngineStatus,
  PoseFrame,
  PoseImageSource,
  PoseRunningMode,
} from './pose-types';

/** How long model + WASM initialisation took. Seeds the PS3-6 tunables; never a requirement. */
export interface PoseEngineInitTiming {
  /** Time to fetch and instantiate the tasks-vision WASM runtime, in ms. */
  readonly wasmLoadMs: number;
  /** Time to fetch the `.task` bundle and build the landmarker, in ms. */
  readonly modelLoadMs: number;
  /** Sum of the two, i.e. what a child would wait for. */
  readonly totalMs: number;
}

export interface PoseEngineInit extends Partial<Omit<PoseEngineOptions, 'runningMode'>> {
  readonly runningMode: PoseRunningMode;
}

const NOT_READY = 'PoseLandmarkerEngine is not ready; call initialise() and await it first.';

/**
 * Owns model loading and per-frame detection.
 *
 * One instance owns one MediaPipe task. `close()` releases it; a closed engine cannot be
 * restarted, because a half-torn-down inference task is exactly the kind of thing that later
 * turns into a camera indicator that stays lit.
 */
export class PoseLandmarkerEngine {
  private readonly options: PoseEngineOptions;
  private readonly nextTimestamp = createMonotonicTimestamps();

  private landmarker: PoseLandmarker | null = null;
  private state: PoseEngineStatus = 'idle';
  private initialising: Promise<PoseEngineInitTiming> | null = null;
  private timing: PoseEngineInitTiming | null = null;

  constructor(init: PoseEngineInit) {
    const assets = assertSameOriginAssets(init.assets ?? POSE_ENGINE_ASSETS);

    this.options = {
      ...POSE_ENGINE_DEFAULTS,
      ...init,
      assets,
    };
  }

  get status(): PoseEngineStatus {
    return this.state;
  }

  get runningMode(): PoseRunningMode {
    return this.options.runningMode;
  }

  /** Initialisation timing, once initialisation has succeeded. */
  get initTiming(): PoseEngineInitTiming | null {
    return this.timing;
  }

  /**
   * Loads the WASM runtime and the model. Safe to call twice: the second caller awaits the first
   * attempt rather than starting a second download of a 9 MB model.
   */
  async initialise(): Promise<PoseEngineInitTiming> {
    if (this.state === 'closed') throw new Error('PoseLandmarkerEngine has been closed.');
    if (this.initialising !== null) return this.initialising;

    this.state = 'loading';
    this.initialising = this.load().catch((error: unknown) => {
      this.state = 'failed';
      // Cleared so a retry is possible after a transient failure (a model 404 during a deploy,
      // say); the engine is only permanently unusable after `close()`.
      this.initialising = null;
      throw error;
    });

    return this.initialising;
  }

  private async load(): Promise<PoseEngineInitTiming> {
    const wasmStart = performance.now();
    const fileset = await FilesetResolver.forVisionTasks(this.options.assets.wasmBasePath);
    const wasmLoadMs = performance.now() - wasmStart;

    const modelStart = performance.now();
    const landmarker = await PoseLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: this.options.assets.modelAssetPath,
        delegate: this.options.delegate,
      },
      runningMode: this.options.runningMode,
      numPoses: this.options.numPoses,
      minPoseDetectionConfidence: this.options.minPoseDetectionConfidence,
      minPosePresenceConfidence: this.options.minPosePresenceConfidence,
      minTrackingConfidence: this.options.minTrackingConfidence,
      outputSegmentationMasks: false,
    });

    const modelLoadMs = performance.now() - modelStart;

    // A `close()` that landed while the model was still loading must win, or the engine ends up
    // holding a live inference task nobody believes exists.
    if (this.state === 'closed') {
      landmarker.close();
      throw new Error('PoseLandmarkerEngine was closed during initialisation.');
    }

    this.landmarker = landmarker;
    this.state = 'ready';
    this.timing = { wasmLoadMs, modelLoadMs, totalMs: wasmLoadMs + modelLoadMs };
    return this.timing;
  }

  /** Runs detection over a still image. Only valid on an `IMAGE`-mode engine. */
  detectImage(source: PoseImageSource): PoseFrame {
    const landmarker = this.requireReady('IMAGE');

    const startedAt = performance.now();
    const result = landmarker.detect(source);
    const detectedAt = performance.now();

    return toPoseFrame(result, {
      timestampMs: this.nextTimestamp(startedAt),
      detectedAt,
      detectionLatencyMs: detectedAt - startedAt,
    });
  }

  /**
   * Runs detection over one video frame. Only valid on a `VIDEO`-mode engine.
   *
   * `timestampMs` should be the frame's own presentation time where the caller has one; it is
   * forced strictly increasing before it reaches MediaPipe.
   */
  detectVideoFrame(source: PoseImageSource, timestampMs: number): PoseFrame {
    const landmarker = this.requireReady('VIDEO');
    const stamp = this.nextTimestamp(timestampMs);

    const startedAt = performance.now();
    const result = landmarker.detectForVideo(source, stamp);
    const detectedAt = performance.now();

    return toPoseFrame(result, {
      timestampMs: stamp,
      detectedAt,
      detectionLatencyMs: detectedAt - startedAt,
    });
  }

  /**
   * Releases the inference task. Idempotent, and the terminal state: nothing detects afterwards.
   *
   * This releases MediaPipe's resources only. Stopping media tracks belongs to whoever acquired
   * the stream (architecture §23; PRV-02) — the engine never owns one, so it cannot pretend to.
   */
  close(): void {
    this.state = 'closed';
    this.landmarker?.close();
    this.landmarker = null;
  }

  private requireReady(expected: PoseRunningMode): PoseLandmarker {
    if (this.options.runningMode !== expected) {
      throw new Error(
        `PoseLandmarkerEngine is in ${this.options.runningMode} mode; ${expected} detection is not available.`,
      );
    }
    if (this.state !== 'ready' || this.landmarker === null) throw new Error(NOT_READY);
    return this.landmarker;
  }
}
